"use client";

import { withParentSize } from "@visx/responsive";
import { scaleTime } from "@visx/scale";
import { Line } from "@visx/shape";
import { Zoom } from "@visx/zoom";
import { bisector } from "d3-array";
import React, { isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  ChartContext,
  defaultAnimationSettings,
  type TooltipData,
  type TooltipSeries,
  useChartEntrance,
  type YFormatType,
} from "../../../context/ChartContext";
import { useChartExport } from "../../../hooks/useChartExport";
import { useColorMode } from "../../../utils/ColorModeProvider";
import {
  type BaseDataPoint,
  createXScale,
  createYScale,
  defaultMargin,
  getYFormatSettings,
  resolveAxisFormatter,
  resolveBaselineValue,
} from "../../../utils/charts";
import { getDefaultChartColor, getThemeCategoricalColors } from "../../../utils/colors";
import type { FieldFormat } from "../../../utils/formatting/types";
import { getMaxTextWidth } from "../../../utils/measureText";
import { Skeleton } from "../../Skeleton";
import { AreaSeries, type AreaSeriesBaseline } from "../AreaSeries";
import { BarSeries } from "../BarSeries";
import { ChartAxis, calculateOptimalYTickCount } from "../ChartAxis";
import { ChartBottomBar } from "../ChartBottomBar";
import { ChartBrush } from "../ChartBrush";
import { ChartEventMarkers } from "../ChartEventMarkers";
import { ChartEventSpans } from "../ChartEventSpans";
import { ChartHeader, useChartHeaderMetrics, useOccupiedHeight } from "../ChartHeader";
import { useChartExpand } from "../ChartHeader/useChartExpand";
import type { ChartSource } from "../ChartSource/chart-source-types";
import { isStreamingSource } from "../ChartSource/chart-source-types";
import { StreamingChart } from "../ChartSource/StreamingChart";
import { ChartTooltip } from "../ChartTooltip";
import { flattenChartChildren } from "../flattenChartChildren";
import { LineSeries } from "../LineSeries";
import { PointSeries } from "../PointSeries";

type SeriesProps =
  | React.ComponentProps<typeof AreaSeries>
  | React.ComponentProps<typeof LineSeries>
  | React.ComponentProps<typeof BarSeries>
  | React.ComponentProps<typeof PointSeries>;

const isSeriesElement = (child: React.ReactElement<unknown>): child is React.ReactElement<SeriesProps> =>
  child.type === AreaSeries || child.type === LineSeries || child.type === BarSeries || child.type === PointSeries;

const isEventMarkersElement = (
  child: React.ReactElement<unknown>
): child is React.ReactElement<
  React.ComponentProps<typeof ChartEventMarkers> | React.ComponentProps<typeof ChartEventSpans>
> => child.type === ChartEventMarkers || child.type === ChartEventSpans;

interface ChartContainerProps {
  children: React.ReactNode;
  parentWidth: number;
  parentHeight: number;
  /**
   * Accessible name for the chart, rendered as the SVG `<title>`. Required so
   * that screen readers announce what each chart shows; every chart needs its
   * own, and there is deliberately no default to fall back to.
   */
  title: string;
  /**
   * Y-axis formatter using the main formatting system
   * Accepts ComponentFormatter types (string shorthand or full options)
   * @example "energy", "currency", { type: "temperature", unit: "F" }
   */
  yFormatter?: YFormatType;
  /**
   * Explicit y-axis domain `[min, max]`. Overrides the default behavior of
   * anchoring the axis at zero for all-positive data. Use for deviation-style
   * metrics (voltage ~240V, frequency ~60Hz, power factor ~1.0) where the
   * signal is variation around a nominal value, not magnitude from zero.
   */
  yDomain?: [number, number];
  /**
   * Horizontal reference lines drawn across the plot at fixed y-values — e.g.
   * regulatory limits (ANSI voltage bounds), targets, or thresholds. Rendered
   * as dashed lines with an optional right-aligned label.
   */
  referenceLines?: { value: number; label?: string; color?: string }[];
  isLoading?: boolean;
  /** Enable scroll-to-zoom and pan on main chart */
  enableZoom?: boolean;
  /** Show brush overview chart below */
  enableBrush?: boolean;
  /** Height of brush chart (default: 60px) */
  brushHeight?: number;
  /**
   * Notification callback when the zoom range changes.
   *
   * Fires on every transition, including the initial `null` render. Use this
   * for one-way "observe the zoom" plumbing (e.g. driving an external panel
   * showing the current window).
   *
   * In controlled-zoom mode (when the `zoomDomain` prop is provided), this is
   * ALSO the change callback the parent uses to update its own state —
   * mirroring the `onHiddenSeriesChange` pattern. The parent should treat the
   * incoming `domain` argument as the next value to commit to its state.
   */
  onZoomChange?: (domain: [Date, Date] | null) => void;
  /**
   * Controlled zoom domain. When provided, ChartContainer uses this value
   * instead of its internal useState (controlled mode). Mirrors the
   * `hiddenSeriesLabels` pattern — useful when an inline + dialog instance
   * pair need to share zoom state (see BOLT-1160 Phase 3 / SiteEnergyCharts).
   *
   * Pass `null` for "full domain, no zoom". Omit entirely to use the default
   * uncontrolled behaviour (ChartContainer manages its own zoom state).
   *
   * When controlled, the visx Zoom transform is kept in sync with the
   * incoming prop via an internal effect, so brush + scroll-zoom + reset
   * all continue to work — they just route the new domain through
   * `onZoomChange` to the parent, which echoes it back via the prop.
   */
  zoomDomain?: [Date, Date] | null;
  /**
   * Controlled set of hidden series labels. When provided, ChartContainer
   * uses this value instead of its internal useState (controlled mode).
   * Omit to use the default uncontrolled behaviour.
   */
  hiddenSeriesLabels?: Set<string>;
  /**
   * Called when the user toggles a legend item in controlled mode.
   * Accepts either the next Set directly or a functional updater
   * `(prev: Set<string>) => Set<string>` — the latter lets parents compose
   * rapid sequential toggles correctly (each sees the latest prev rather than
   * the stale render-closure value). Only called when `hiddenSeriesLabels`
   * prop is provided.
   */
  onHiddenSeriesChange?: (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  /**
   * Hide the bottom bar (legend + export button). Useful for small/embedded
   * charts — e.g. inline sparklines — where the legend and export affordance
   * add unwanted chrome. Defaults to false (bottom bar shown) to preserve
   * existing behavior for all current consumers.
   */
  hideBottomBar?: boolean;
  /** Called on a click in the plot: the x value under the pointer and the tooltip row drawn nearest to it. */
  onPointClick?: (xValue: Date, series: TooltipSeries) => void;
  /** The unzoomed x-domain of a fed chart; defaults to the span of the plotted data. A streaming source drives its own window and ignores it. */
  xDomain?: [Date, Date];
  /** Trailing actions rendered in the `ChartHeader`, opposite the title. */
  headerActions?: React.ReactNode;
  /** Control cluster rendered in the `ChartHeader` — `TimeControls` today. */
  headerControls?: React.ReactNode;
}

/**
 * Default plot-body height for a chart block, in px. Pairs with `CHART_HEADER_HEIGHT`.
 *
 * It lives here rather than in each block because four blocks had four private copies of `280`
 * that happened to agree, with nothing enforcing the agreement — a design change to chart height
 * was a four-file diff where three files could be forgotten and nothing failed. This is `P10 R-23`.
 *
 * A block that needs a different body height passes its own; this is the value it starts from.
 */
export const DEFAULT_CHART_HEIGHT = 280;

const bisectDate = bisector<BaseDataPoint, Date>((d) => d.xValue).left;

const ChartEmptyState: React.FC = () => (
  <div className="flex h-full items-center justify-center">
    <p className="text-text-secondary">No data available</p>
  </div>
);

const ChartLoading: React.FC = () => (
  <div className="flex h-full flex-col gap-4 p-4">
    <Skeleton className="h-full w-full" />
    <div className="flex gap-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-24" />
    </div>
  </div>
);

const POINT_HIT_PX = 8;

const VisXChartContainer: React.FC<ChartContainerProps> = ({
  parentWidth,
  parentHeight,
  yFormatter = { type: "number" },
  yDomain,
  referenceLines,
  isLoading = false,
  enableZoom = false,
  enableBrush = false,
  brushHeight = 60,
  onZoomChange,
  zoomDomain: controlledZoomDomain,
  hiddenSeriesLabels: controlledHiddenSeriesLabels,
  onHiddenSeriesChange,
  hideBottomBar = false,
  onPointClick,
  xDomain,
  headerActions,
  headerControls,
  title,
  children,
}) => {
  const isControlled = controlledHiddenSeriesLabels !== undefined;
  // Zoom controlled mode: parent owns `zoomDomain` state and re-feeds it via
  // the prop after `onZoomChange` callbacks. We still keep an internal
  // useState for the uncontrolled default path, and resolve which value is
  // active each render. Mirrors the hiddenSeriesLabels pattern above.
  const isZoomControlled = controlledZoomDomain !== undefined;
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [internalZoomDomain, setInternalZoomDomain] = useState<[Date, Date] | null>(null);
  const zoomDomain = isZoomControlled ? controlledZoomDomain : internalZoomDomain;
  const [internalHiddenSeriesLabels, setInternalHiddenSeriesLabels] = useState<Set<string>>(() => new Set());
  // Resolve which hidden set is active: controlled value wins when provided.
  const hiddenSeriesLabels = isControlled ? controlledHiddenSeriesLabels : internalHiddenSeriesLabels;
  const svgRef = useRef<SVGSVGElement>(null);
  const plotClipId = useId();
  const lastMoveRef = useRef<number>(0);
  const lastTransformRef = useRef<string>("");
  // biome-ignore lint/suspicious/noExplicitAny: Zoom instance type from visx is complex and not fully exported
  const zoomRef = useRef<any>(null); // Store zoom instance to programmatically control it

  // Get color mode to trigger re-render when theme changes
  const { colorMode } = useColorMode();

  // Get theme-aware colors
  const themeColors = useMemo(() => getThemeCategoricalColors(), [colorMode]);
  const defaultColor = useMemo(() => getDefaultChartColor(), [colorMode]);

  // Extract datasets and metadata from children, and assign colors
  const { datasets, seriesMetadata, categoryInfo, categoryColors, categoryLabels, seriesTypes } = useMemo(() => {
    const childArray = flattenChartChildren(children);
    const seriesChildren = childArray.filter(
      (child): child is React.ReactElement<SeriesProps> => isValidElement(child) && isSeriesElement(child)
    );

    const totalSeries = seriesChildren.filter((child) => child.props.data).length;
    const data: BaseDataPoint[][] = [];
    const metadata: Array<{
      label: string;
      color: string;
      baseline?: AreaSeriesBaseline;
      tooltipRange?: boolean;
    }> = [];
    const seriesTypes: Array<"area" | "line" | "bar" | "point"> = [];
    let categoryColors: Record<string, string> | null = null;
    let categoryLabels: Record<string, string> | null = null;

    seriesChildren.forEach((child, seriesIndex) => {
      const childProps = child.props as SeriesProps;
      if (!childProps.data) {
        return;
      }

      data.push(childProps.data as BaseDataPoint[]);

      const color = "color" in childProps && childProps.color ? childProps.color : undefined;
      const resolvedColor = color || (totalSeries === 1 ? defaultColor : themeColors[seriesIndex % themeColors.length]);

      const isArea = child.type === AreaSeries;
      metadata.push({
        label: ("label" in childProps && childProps.label) || `Series ${data.length}`,
        color: resolvedColor,
        ...(isArea && "baseline" in childProps && childProps.baseline !== undefined
          ? {
              baseline: childProps.baseline,
              tooltipRange: ("tooltipRange" in childProps && childProps.tooltipRange) || false,
            }
          : {}),
      });

      const seriesType = isArea
        ? "area"
        : child.type === LineSeries
          ? "line"
          : child.type === PointSeries
            ? "point"
            : "bar";
      seriesTypes.push(seriesType);

      // Check if this child has categoryColors/categoryLabels (any series with category-based coloring)
      if ("categoryColors" in childProps && childProps.categoryColors) {
        categoryColors = childProps.categoryColors as Record<string, string>;
      }
      if ("categoryLabels" in childProps && childProps.categoryLabels) {
        categoryLabels = childProps.categoryLabels as Record<string, string>;
      }
    });

    // Extract unique categories if categoryColors is being used
    let categories: Array<{ label: string; color: string }> | null = null;
    if (categoryColors) {
      const categorySet = new Set<string>();
      data.forEach((dataset) => {
        dataset.forEach((point) => {
          if (point.category) {
            categorySet.add(point.category);
          }
        });
      });

      categories = Array.from(categorySet).map((category) => ({
        label: categoryLabels?.[category] || category,
        color: categoryColors![category] || "#6b7280",
      }));
    }

    return {
      datasets: data,
      seriesMetadata: metadata,
      categoryInfo: categories,
      categoryColors: categoryColors,
      categoryLabels: categoryLabels,
      seriesTypes,
    };
  }, [children, defaultColor, themeColors, colorMode]);

  const expand = useChartExpand(title);
  // Expanded, the HOC's container stays behind in the layout and keeps
  // reporting the inline size; the body is measured where it actually is.
  const width = expand.expandedSize?.width ?? parentWidth;
  const measuredParentHeight = expand.expandedSize?.height ?? parentHeight;
  // Subtract ChartHeader (when it renders) + ChartBottomBar height (60px, when
  // shown) + optional brush height
  // `hideBottomBar` already marks a chart as deliberately chrome-less — inline
  // sparklines, the 130px monitor preview. Those get no expand either, rather
  // than introducing a second opt-out for the same idea.
  const showExpand = !hideBottomBar;
  // Resolve once. `expand.action` is null when an ancestor owns expand, so an
  // unconditional fragment would be a truthy `actions` prop wrapping nothing —
  // ChartHeader would render an empty 48px bar while the height math reserved
  // zero for it, and the chrome-less charts would overflow.
  const resolvedExpandAction = showExpand ? expand.action : null;
  const resolvedActions =
    headerActions || resolvedExpandAction ? (
      <>
        {headerActions}
        {resolvedExpandAction}
      </>
    ) : undefined;
  const { headerRef, headerHeight } = useChartHeaderMetrics(Boolean(resolvedActions), Boolean(headerControls));
  const { ref: bottomBarRef, height: measuredBottomBarHeight } = useOccupiedHeight();
  // First paint uses the single-row bar (32px) plus its 8px top gap.
  const bottomBarHeight = hideBottomBar ? 0 : measuredBottomBarHeight || 40;
  const brushHeightAdjustment = enableBrush ? brushHeight + 8 : 0; // 8px for spacing
  const calculatedHeight = measuredParentHeight - headerHeight - bottomBarHeight - brushHeightAdjustment;
  // Ensure minimum height of 100px to avoid negative values
  const height = Math.max(100, calculatedHeight);

  const isSeriesHidden = useCallback(
    (label: string | undefined) => (label ? hiddenSeriesLabels.has(label) : false),
    [hiddenSeriesLabels]
  );

  const visibleSeriesIndexes = useMemo(
    () =>
      seriesMetadata
        .map((meta, index) => (isSeriesHidden(meta.label) ? null : index))
        .filter((index): index is number => index !== null),
    [seriesMetadata, isSeriesHidden]
  );

  const visibleDatasets = useMemo(
    () =>
      visibleSeriesIndexes
        .map((index) => datasets[index])
        .filter((dataset): dataset is BaseDataPoint[] => Boolean(dataset)),
    [datasets, visibleSeriesIndexes]
  );

  const visibleSeriesMetadata = useMemo(
    () =>
      visibleSeriesIndexes
        .map((index) => seriesMetadata[index])
        .filter(
          (meta): meta is { label: string; color: string; baseline?: AreaSeriesBaseline; tooltipRange?: boolean } =>
            Boolean(meta)
        ),
    [seriesMetadata, visibleSeriesIndexes]
  );

  const visibleSeriesTypes = useMemo(
    () =>
      visibleSeriesIndexes
        .map((index) => seriesTypes[index])
        .filter((type): type is "area" | "line" | "bar" | "point" => Boolean(type)),
    [seriesTypes, visibleSeriesIndexes]
  );

  const firstVisibleBarSeriesIndex = visibleSeriesTypes.findIndex((type) => type === "bar");
  const hasVisibleBarSeries = firstVisibleBarSeriesIndex !== -1;

  // Shared transition: given the previous hidden set and the label being
  // toggled, return the next hidden set — or `previous` unchanged if the
  // toggle would hide the last visible series (last-visible guard).
  //
  // Used by both the controlled and uncontrolled code paths below so the
  // guard logic exists in exactly one place. Pure function: depends only on
  // its arguments + the current `seriesMetadata` closure.
  const computeNextHidden = useCallback(
    (previous: Set<string>, label: string): Set<string> => {
      const wasHidden = previous.has(label);
      if (!wasHidden) {
        const visibleAfterHide = seriesMetadata.reduce(
          (count, meta) => (previous.has(meta.label) || meta.label === label ? count : count + 1),
          0
        );
        if (visibleAfterHide < 1) {
          // Guard: would hide the last visible series — no-op.
          return previous;
        }
      }

      const next = new Set(previous);
      if (wasHidden) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    },
    [seriesMetadata]
  );

  const handleLegendItemToggle = useCallback(
    (label: string) => {
      if (isControlled) {
        // Eager guard: check against the current prop whether hiding `label`
        // would remove the last visible series. If so, skip the callback
        // entirely so the parent is never asked to perform a no-op transition.
        // This is a perf optimization layered on top of the in-updater guard
        // below — the updater guard is still required for correctness under
        // rapid sequential toggles (see comment on the functional updater).
        const currentHidden = controlledHiddenSeriesLabels!;
        const isCurrentlyHidden = currentHidden.has(label);
        if (!isCurrentlyHidden) {
          const visibleAfterHide = seriesMetadata.reduce(
            (count, meta) => (currentHidden.has(meta.label) || meta.label === label ? count : count + 1),
            0
          );
          if (visibleAfterHide < 1) {
            setTooltipData(null);
            return;
          }
        }
        // Pass a functional updater so that rapid sequential toggles before
        // the parent re-renders each compose from the latest prev rather than
        // the stale render-closure value, preventing a second hide from
        // dropping an earlier one. `computeNextHidden` re-applies the
        // last-visible guard against `previous`, which may already include
        // another rapid toggle.
        onHiddenSeriesChange?.((previous) => computeNextHidden(previous, label));
      } else {
        // Uncontrolled: same transition, applied to internal state. The
        // functional updater here uses `previous` rather than the render-
        // closure `visibleSeriesIndexes` so a second hide that arrives before
        // re-render still sees the freshly-toggled visible count.
        setInternalHiddenSeriesLabels((previous) => computeNextHidden(previous, label));
      }
      setTooltipData(null);
    },
    [isControlled, controlledHiddenSeriesLabels, onHiddenSeriesChange, seriesMetadata, computeNextHidden]
  );

  // Reset hidden-series state when the underlying series labels change.
  // Without this, hiding e.g. `consumption` for site A and then switching to
  // site B (which renders the same labels but represents different data) would
  // carry the hidden state across, even though `ChartContainer` itself is not
  // remounted by the consumer.
  const seriesLabelsKey = useMemo(() => seriesMetadata.map((meta) => meta.label).join("\u0001"), [seriesMetadata]);
  const previousSeriesLabelsKey = useRef<string>(seriesLabelsKey);
  useEffect(() => {
    if (previousSeriesLabelsKey.current !== seriesLabelsKey) {
      previousSeriesLabelsKey.current = seriesLabelsKey;
      // Uncontrolled path: reset internal state when series labels change.
      // Controlled path: the parent is responsible for resetting the prop.
      if (!isControlled) {
        setInternalHiddenSeriesLabels((previous) => (previous.size === 0 ? previous : new Set()));
      }
    }
  }, [seriesLabelsKey, isControlled]);

  // Combine visible datasets for scale creation
  const allData = useMemo(() => visibleDatasets.flat(), [visibleDatasets]);

  const dataDomain = useMemo<[Date, Date] | null>(() => {
    if (allData.length === 0) return null;
    const sortedData = [...allData].sort((a, b) => a.xValue.getTime() - b.xValue.getTime());
    return [sortedData[0].xValue, sortedData[sortedData.length - 1].xValue];
  }, [allData]);
  const pinnedDomain = xDomain ?? dataDomain;
  const fullStartMs = pinnedDomain?.[0].getTime();
  const fullEndMs = pinnedDomain?.[1].getTime();
  const fullDomain = useMemo<[Date, Date] | null>(
    () => (fullStartMs === undefined || fullEndMs === undefined ? null : [new Date(fullStartMs), new Date(fullEndMs)]),
    [fullStartMs, fullEndMs]
  );

  // Current visible domain (zoomed or full)
  const visibleDomain = zoomDomain || fullDomain;

  const scaleDomain = visibleDomain;

  // Commit a new zoom domain. In uncontrolled mode this writes to internal
  // state and the existing `onZoomChange` effect (below) notifies the parent.
  // In controlled mode we skip the internal write and call `onZoomChange`
  // directly — the parent owns state, will update its own value, and feed it
  // back via the `zoomDomain` prop, which the visx-transform-sync effect
  // (below) will then propagate into the Zoom instance.
  const commitZoomDomain = useCallback(
    (next: [Date, Date] | null) => {
      if (isZoomControlled) {
        onZoomChange?.(next);
      } else {
        setInternalZoomDomain(next);
      }
    },
    [isZoomControlled, onZoomChange]
  );

  // Filter data to visible domain for performance.
  const filteredDatasets = useMemo(() => {
    if (!scaleDomain) return datasets;

    const filtered = datasets.map((seriesData) =>
      seriesData.filter((d) => d.xValue >= scaleDomain[0] && d.xValue <= scaleDomain[1])
    );

    return filtered;
  }, [datasets, scaleDomain]);

  // Visible series, zoom-trimmed, in the same order as visibleSeriesMetadata.
  // AreaSeries draws its band from the filtered data, so resolving a function
  // baseline for the tooltip must use this same (filtered) point/index — else a
  // zoomed range tooltip can desync from the drawn band.
  const visibleFilteredDatasets = useMemo(
    () => visibleSeriesIndexes.map((index) => filteredDatasets[index] ?? []),
    [visibleSeriesIndexes, filteredDatasets]
  );

  // Notify parent of zoom changes
  useEffect(() => {
    if (onZoomChange) {
      onZoomChange(zoomDomain);
    }
  }, [zoomDomain, onZoomChange]);

  // Use filtered data for y-scale to get accurate range for visible data
  const yScaleData = useMemo(
    () => visibleSeriesIndexes.flatMap((index) => filteredDatasets[index] ?? []),
    [filteredDatasets, visibleSeriesIndexes]
  );
  const yScale = useMemo(
    () => createYScale(yScaleData, height, yFormatter, yDomain),
    [yScaleData, height, yFormatter, yDomain]
  );

  // ── Pinned Y-axis unit (BOLT-1432) ──────────────────────────────────────
  // The axis unit is chosen ONCE on mount (from the initial domain max) and
  // held constant across zoom / pan changes so ticks never flip between
  // e.g. Wh → kWh → MWh as the visible window moves.
  //
  // We compare the incoming yFormatter by JSON value rather than object
  // identity so callers who write `yFormatter={{ type: "energy" }}` inline
  // (which creates a new JS object on every render) don't accidentally reset
  // the pin on every render — only a genuine semantic change (type, unit,
  // currency, etc.) triggers a re-pin from the current domain max.
  const yFormatterKeyRef = useRef<string>(JSON.stringify(yFormatter));
  const pinnedFormatterRef = useRef<YFormatType | null>(null);

  const axisFormatter = useMemo(() => {
    const [d0, d1] = yScale.domain() as [number, number];
    const maxAbsValue = Math.max(Math.abs(d0), Math.abs(d1));

    const currentKey = JSON.stringify(yFormatter);
    if (currentKey !== yFormatterKeyRef.current) {
      yFormatterKeyRef.current = currentKey;
      pinnedFormatterRef.current = null;
    }

    if (pinnedFormatterRef.current !== null) {
      return pinnedFormatterRef.current;
    }

    // Defer pinning when the domain is degenerate (no data yet / all zeros).
    // Pinning on an empty domain would lock in the smallest unit (e.g. Wh)
    // before real telemetry arrives, and the pin would never update once set.
    if (maxAbsValue === 0) {
      return resolveAxisFormatter(yFormatter, 0);
    }

    const resolved = resolveAxisFormatter(yFormatter, maxAbsValue);
    pinnedFormatterRef.current = resolved;
    return resolved;
  }, [yScale, yFormatter]);
  // ────────────────────────────────────────────────────────────────────────

  // Calculate optimal Y-axis tick count based on available height
  // Use estimated inner height (height minus default margins) for initial calculation
  const yTickCount = useMemo(() => {
    const estimatedInnerHeight = height - defaultMargin.top - defaultMargin.bottom;
    return calculateOptimalYTickCount(estimatedInnerHeight);
  }, [height]);

  // Calculate dynamic left margin based on y-axis label widths
  const dynamicMargin = useMemo(() => {
    const formatSettings = getYFormatSettings(axisFormatter);
    const tickFormat = formatSettings.tickFormat || formatSettings.format;

    // Get tick values using optimal tick count
    const tickValues = yScale.ticks(yTickCount);
    const formattedTicks = tickValues.map((value) => tickFormat(value));

    // Measure the widest label
    const maxLabelWidth = getMaxTextWidth(formattedTicks, 12, "system-ui");

    // Add minimal padding: tick mark (8px) + small gap (4px)
    // Labels are right-aligned so they sit flush against the margin
    const calculatedLeftMargin = Math.ceil(maxLabelWidth) + 12;

    return {
      ...defaultMargin,
      left: Math.max(calculatedLeftMargin, 30), // Minimum 30px for very small values
      // Air between the plot's right edge and the container border, so points
      // at the domain max don't sit on the border (mirrors defaultMargin.top).
      right: 8,
    };
  }, [yScale, axisFormatter, yTickCount]);

  // Create scales based on the visible domain. Uses dynamicMargin.
  const xScale = useMemo(() => {
    if (!scaleDomain) return createXScale(allData, width);

    return scaleTime({
      domain: scaleDomain,
      range: [dynamicMargin.left, width - dynamicMargin.right],
    });
  }, [scaleDomain, allData, width, dynamicMargin]);

  // Update zoom domain when transform changes
  const handleZoomTransform = useCallback(
    (transformMatrix: {
      scaleX: number;
      scaleY: number;
      translateX: number;
      translateY: number;
      skewX: number;
      skewY: number;
    }) => {
      if (!fullDomain) return;

      const { scaleX, translateX } = transformMatrix;

      // Calculate the visible range in pixel space
      const chartWidth = width - dynamicMargin.left - dynamicMargin.right;
      const [minX, maxX] = fullDomain;
      const totalDuration = maxX.getTime() - minX.getTime();

      // Guard against single-point or invalid domain
      if (totalDuration <= 0) {
        commitZoomDomain(null);
        return;
      }

      // Apply inverse transform to get the data domain visible in the viewport
      // The transform maps data space to screen space, so we need to invert it
      const visibleStartPixel = -translateX;
      const visibleEndPixel = visibleStartPixel + chartWidth;

      // Convert pixel positions back to data domain
      const pixelsPerMs = (chartWidth * scaleX) / totalDuration;
      const startOffset = visibleStartPixel / pixelsPerMs;
      const endOffset = visibleEndPixel / pixelsPerMs;

      const newMin = new Date(minX.getTime() + startOffset);
      const newMax = new Date(minX.getTime() + endOffset);

      // Check if the new domain is essentially the same as the full domain
      // If so, set zoomDomain to null (no zoom)
      const minDiff = Math.abs(newMin.getTime() - minX.getTime());
      const maxDiff = Math.abs(newMax.getTime() - maxX.getTime());
      const threshold = totalDuration * 0.02; // 2% of total duration

      if (minDiff < threshold && maxDiff < threshold) {
        commitZoomDomain(null);
      } else {
        commitZoomDomain([newMin, newMax]);
      }
    },
    [fullDomain, width, dynamicMargin, commitZoomDomain]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const { left } = svgRef.current?.getBoundingClientRect() || { left: 0 };
      const mouseX = event.clientX - left;

      // Throttle the mousemove event
      if (event.timeStamp - (lastMoveRef.current || 0) < 16) {
        return;
      }
      lastMoveRef.current = event.timeStamp;

      // Find the closest x value
      const xValue = xScale.invert(mouseX);

      // Find the nearest points from all series
      const seriesData = visibleFilteredDatasets
        .map((data, datasetIndex) => {
          if (!data || data.length === 0) return null;

          const idx = bisectDate(data, xValue, 1);
          if (idx === 0) return { point: data[0], index: 0, datasetIndex };
          if (idx >= data.length) return { point: data[data.length - 1], index: data.length - 1, datasetIndex };

          const d0 = data[idx - 1];
          const d1 = data[idx];
          if (!d0 || !d1) return null;

          const distanceToD0 = Math.abs(d0.xValue.getTime() - xValue.getTime());
          const distanceToD1 = Math.abs(d1.xValue.getTime() - xValue.getTime());
          const closer0 = distanceToD0 < distanceToD1;

          return { point: closer0 ? d0 : d1, index: closer0 ? idx - 1 : idx, datasetIndex };
        })
        .filter(
          (item): item is { point: BaseDataPoint; index: number; datasetIndex: number } =>
            item !== null && visibleSeriesMetadata[item.datasetIndex] !== undefined
        )
        .map(({ point, index, datasetIndex }) => ({
          label: visibleSeriesMetadata[datasetIndex].label,
          value: point.yValue,
          baselineValue: resolveBaselineValue(visibleSeriesMetadata[datasetIndex], point, index),
          color: visibleSeriesMetadata[datasetIndex].color,
          point,
          type: visibleSeriesTypes[datasetIndex],
        }));

      if (seriesData.length === 0) {
        setTooltipData(null);
        return;
      }

      const tooltipX = xScale(seriesData[0].point.xValue);
      const shouldShowLeft = tooltipX > (width - dynamicMargin.right - dynamicMargin.left) / 2 + dynamicMargin.left;

      setTooltipData({
        xValue: seriesData[0].point.xValue,
        series: seriesData.map(({ label, value, baselineValue, color, point, type }) => ({
          label,
          value,
          xValue: point.xValue,
          ...(baselineValue !== undefined ? { baselineValue } : {}),
          // Render-only override for stacked bands: place the focus highlight
          // on the drawn stack coordinate (chartYValue) rather than the data
          // value. Falls back to `value` for non-stacked series.
          chartY: point.chartYValue ?? value,
          showMarker: point.showTooltipMarker ?? true,
          color,
          type,
          category: point.category,
          categoryColor: point.category && categoryColors ? categoryColors[point.category] : undefined,
          ...(point.extraTooltipRows && point.extraTooltipRows.length > 0
            ? { extraTooltipRows: point.extraTooltipRows }
            : {}),
        })),
        x: tooltipX,
        y: yScale(seriesData[0].point.chartYValue ?? seriesData[0].value),
        position: shouldShowLeft ? "left" : "right",
      });
    },
    [
      visibleFilteredDatasets,
      visibleSeriesMetadata,
      visibleSeriesTypes,
      xScale,
      yScale,
      width,
      categoryColors,
      dynamicMargin,
    ]
  );

  const handleMouseLeave = () => {
    setTooltipData(null);
  };

  const handlePointClick = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!tooltipData || !onPointClick) return;
      const rect = svgRef.current?.getBoundingClientRect();
      const mouseX = event.clientX - (rect?.left ?? 0);
      const mouseY = event.clientY - (rect?.top ?? 0);
      const underPointer = (row: TooltipSeries) =>
        row.type === "point" && row.xValue !== undefined && Math.abs(xScale(row.xValue) - mouseX) <= POINT_HIT_PX;
      let nearest: TooltipSeries | null = null;
      let nearestDistance = Number.POSITIVE_INFINITY;
      for (const row of tooltipData.series) {
        const distance = Math.abs(yScale(row.chartY ?? row.value) - mouseY);
        const tied = nearest !== null && Math.abs(distance - nearestDistance) <= 1;
        const pointWinsTie = tied && underPointer(row) && !(nearest !== null && underPointer(nearest));
        const pointKeepsTie = tied && nearest !== null && underPointer(nearest) && !underPointer(row);
        if (pointWinsTie || (distance < nearestDistance && !pointKeepsTie)) {
          nearest = row;
          nearestDistance = distance;
        }
      }
      if (!nearest) return;
      // Series need not share x timestamps (a PointSeries scattered over a
      // LineSeries does not), so report the clicked row's own sample rather
      // than the tooltip's first-series x. See TooltipSeries.xValue.
      onPointClick(nearest.xValue ?? tooltipData.xValue, nearest);
    },
    [tooltipData, onPointClick, xScale, yScale]
  );

  const handleResetZoom = useCallback(() => {
    commitZoomDomain(null);
    // Also reset the zoom transform
    if (zoomRef.current) {
      zoomRef.current.reset();
    }
  }, [commitZoomDomain]);

  /**
   * Synchronize the visx Zoom instance's transform matrix to a target data
   * domain. Used by:
   *   - the brush handler, when the user drags a window selection
   *   - the controlled-zoom effect (below), when the parent feeds in a new
   *     domain via the `zoomDomain` prop
   *
   * No-op when the Zoom ref isn't ready yet or `fullDomain` is unset.
   * Passing `null` resets to the identity transform (full domain).
   */
  /**
   * The visx transform matrix that renders `domain`, or identity for null.
   *
   * Also seeds a freshly-mounted `Zoom`. Expanding moves the body into a
   * Dialog, which remounts `Zoom` below the surviving ChartContainer state — a
   * fresh identity transform then differs from `lastTransformRef` and reads as
   * a user reset, clearing the zoom the user still expects (Codex P1 on
   * #14451). Seeding the remount with the domain's own transform means the key
   * matches, nothing looks changed, and nothing is cleared.
   */
  const transformForDomain = useCallback(
    (domain: [Date, Date] | null) => {
      const identity = { scaleX: 1, scaleY: 1, translateX: 0, translateY: 0, skewX: 0, skewY: 0 };
      if (!domain || !fullDomain) return identity;

      const [minX, maxX] = fullDomain;
      const totalDuration = maxX.getTime() - minX.getTime();
      if (totalDuration <= 0) return identity;

      const [newMin, newMax] = domain;
      const visibleDuration = newMax.getTime() - newMin.getTime();
      if (visibleDuration <= 0) return identity;

      const scaleX = totalDuration / visibleDuration;
      const chartWidth = width - dynamicMargin.left - dynamicMargin.right;
      const startOffset = newMin.getTime() - minX.getTime();
      return {
        scaleX,
        scaleY: 1,
        translateX: -(startOffset / totalDuration) * chartWidth * scaleX,
        translateY: 0,
        skewX: 0,
        skewY: 0,
      };
    },
    [fullDomain, width, dynamicMargin]
  );

  const syncTransformToDomain = useCallback(
    (domain: [Date, Date] | null) => {
      if (!(zoomRef.current && fullDomain)) return;

      const [minX, maxX] = fullDomain;
      const totalDuration = maxX.getTime() - minX.getTime();

      // Guard against single-point or invalid domain
      if (totalDuration <= 0) {
        zoomRef.current.reset();
        return;
      }

      if (!domain) {
        // Reset zoom to identity transform
        zoomRef.current.reset();
        return;
      }

      // Calculate the transform that corresponds to this domain
      const [newMin, newMax] = domain;
      const visibleDuration = newMax.getTime() - newMin.getTime();

      // Guard against invalid selection
      if (visibleDuration <= 0) {
        zoomRef.current.reset();
        return;
      }

      const scaleX = totalDuration / visibleDuration;

      // Calculate translateX
      const chartWidth = width - dynamicMargin.left - dynamicMargin.right;
      const startOffset = newMin.getTime() - minX.getTime();
      const translateX = -(startOffset / totalDuration) * chartWidth * scaleX;

      // Apply the transform
      zoomRef.current.setTransformMatrix({
        scaleX,
        scaleY: 1,
        translateX,
        translateY: 0,
        skewX: 0,
        skewY: 0,
      });
    },
    [fullDomain, width, dynamicMargin]
  );

  const handleBrushChange = useCallback(
    (domain: [Date, Date] | null) => {
      commitZoomDomain(domain);
      // Programmatically update the zoom transform to match the brush selection
      syncTransformToDomain(domain);
    },
    [commitZoomDomain, syncTransformToDomain]
  );

  // Controlled-zoom sync: when the parent feeds a new `zoomDomain` value via
  // the prop, mirror it into the visx Zoom instance so the visual transform
  // matches what the data layer believes. Only runs in controlled mode —
  // uncontrolled mode already updates the transform from user gestures
  // directly (scroll-zoom calls `handleZoomTransform` after the gesture; brush
  // calls `syncTransformToDomain` inside `handleBrushChange`).
  //
  // No-op when the prop matches the value the user just produced (the parent
  // echoed our `onZoomChange` callback right back) — setTransformMatrix is
  // idempotent for the matching matrix and visx tolerates the round-trip.
  useEffect(() => {
    if (!isZoomControlled) return;
    syncTransformToDomain(controlledZoomDomain ?? null);
  }, [isZoomControlled, controlledZoomDomain, syncTransformToDomain]);

  const entrance = useChartEntrance();

  const contextValue = {
    width,
    height,
    xScale,
    yScale,
    yFormatter: axisFormatter,
    animationSettings: defaultAnimationSettings,
    entrance,
    tooltip: {
      data: tooltipData,
      show: setTooltipData,
      hide: () => setTooltipData(null),
    },
    datasets: visibleFilteredDatasets,
    seriesMetadata: visibleSeriesMetadata,
    seriesTypes: visibleSeriesTypes,
    categoryColors: categoryColors || undefined,
    categoryLabels: categoryLabels || undefined,
  };

  // Separate series children from event markers and other children
  const childArray = flattenChartChildren(children);
  let seriesIndex = 0;
  const seriesChildren: React.ReactNode[] = [];
  const eventMarkerChildren: React.ReactNode[] = [];
  const otherChildren: React.ReactNode[] = [];

  childArray.forEach((child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
      return;
    }

    if (isEventMarkersElement(child)) {
      // Collect event markers to render later (on top)
      eventMarkerChildren.push(child);
    } else if (isSeriesElement(child)) {
      // Clone series with assigned colors and filtered data
      const currentSeriesIndex = seriesIndex;
      seriesIndex++;
      const childProps = child.props as SeriesProps;
      const label = seriesMetadata[currentSeriesIndex]?.label;
      if (isSeriesHidden(label)) {
        return;
      }

      seriesChildren.push(
        React.cloneElement(child, {
          ...childProps,
          data: filteredDatasets[currentSeriesIndex] || childProps.data,
          color: seriesMetadata[currentSeriesIndex]?.color,
        } as Partial<SeriesProps>)
      );
    } else {
      // Other children (axes, etc.) render outside the plot clip
      otherChildren.push(child);
    }
  });

  // Get unique series for the legend
  // Use categoryInfo if available (for category-based coloring), otherwise use seriesMetadata
  const visibleSeriesCount = visibleSeriesIndexes.length;
  const uniqueSeries = (categoryInfo || seriesMetadata).map((meta) => {
    const isHidden = !categoryInfo && isSeriesHidden(meta.label);

    return {
      category: meta.label,
      fill: meta.color,
      isHidden,
      isDisabled: !categoryInfo && !isHidden && visibleSeriesCount <= 1,
    };
  });

  // Handle export
  //
  // Pass `yFormatter` through as `yFormat` in the CSV metadata so column
  // headers get a stable unit suffix (e.g. "Consumption (kWh)") and cells
  // are converted into that unit — matching the BOLT-1431 Explore CSV
  // treatment. Only pass through when `yFormatter` is a FieldFormat spec
  // (has a `type` field); a ReactNode-returning function is not something
  // csvUnitFor knows how to interpret and would fall back to raw values.
  const yFormatIsFieldFormat =
    typeof yFormatter === "object" &&
    yFormatter !== null &&
    typeof (yFormatter as { type?: unknown }).type === "string";
  const handleExport = useChartExport({
    datasets: visibleDatasets,
    metadata: {
      xLabel: "Date",
      yLabel: "Value",
      seriesLabels: visibleSeriesMetadata.map((s) => s.label),
      timestamp: true,
      ...(yFormatIsFieldFormat ? { yFormat: yFormatter as FieldFormat } : {}),
    },
    svgRef,
  });

  // The header outlives the plot: an expand action or an owned title that
  // vanishes while the chart is loading or empty is the same disappearing
  // chrome this component exists to stop. Every branch renders it as the provider's first child:
  // moving it remounts the header controls on each load and drops their state.
  const header = (
    <ChartHeader title={title} controls={headerControls} actions={resolvedActions} containerRef={headerRef} />
  );

  // Return loading state if isLoading is true
  if (isLoading) {
    return expand.present(
      <div className="relative flex h-full flex-col">
        <ChartContext.Provider value={contextValue}>
          {header}
          <ChartLoading />
        </ChartContext.Provider>
      </div>
    );
  }

  // Check if we have any data
  if (datasets.length === 0) {
    return expand.present(
      <div className="relative flex h-full flex-col">
        <ChartContext.Provider value={contextValue}>
          {header}
          <ChartEmptyState />
        </ChartContext.Provider>
      </div>
    );
  }

  // SVG content to render (shared between zoom and non-zoom)
  const chartContent = (
    <>
      <title>{title}</title>
      {/* Grid lines */}
      <g>
        {yScale.ticks(yTickCount).map((tickValue) => {
          const isZeroLine = Math.abs(tickValue) < Number.EPSILON;

          return (
            <Line
              key={`grid-line-${tickValue}`}
              from={{ x: dynamicMargin.left, y: yScale(tickValue) }}
              to={{ x: width - dynamicMargin.right, y: yScale(tickValue) }}
              stroke={isZeroLine ? "var(--color-border-default)" : "var(--color-border-muted)"}
              strokeWidth={isZeroLine ? 1.5 : 1}
            />
          );
        })}
      </g>

      {/* Render series, clipped to the plot area */}
      <defs>
        <clipPath id={plotClipId}>
          <rect
            x={xScale.range()[0]}
            y={yScale.range()[1]}
            width={xScale.range()[1] - xScale.range()[0]}
            height={yScale.range()[0] - yScale.range()[1]}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${plotClipId})`}>{seriesChildren}</g>
      {otherChildren}

      {/* Reference lines (regulatory limits, thresholds) — drawn after the
          series so filled areas (AreaSeries) never cover the threshold lines
          or their labels. */}
      {referenceLines && referenceLines.length > 0 && (
        <g>
          {referenceLines.map((ref, index) => {
            const y = yScale(ref.value);
            const stroke = ref.color ?? "var(--color-error-base)";
            return (
              // Keyed by INDEX, not by value: two lines may legitimately share a value — a symmetric
              // pair around zero collapses to one key, and React then renders one line instead of two.
              // The array is ordered and re-keyed wholesale on every render, so the index is stable
              // enough for a decoration that carries no state.
              // biome-ignore lint/suspicious/noArrayIndexKey: values are not unique; see above
              <g key={`ref-line-${index}`}>
                <Line
                  from={{ x: dynamicMargin.left, y }}
                  to={{ x: width - dynamicMargin.right, y }}
                  stroke={stroke}
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                />
                {ref.label && (
                  <text x={width - dynamicMargin.right - 4} y={y - 4} textAnchor="end" fontSize={11} fill={stroke}>
                    {ref.label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      )}

      {/* Axes */}
      <ChartAxis.Bottom
        top={height - dynamicMargin.bottom}
        scale={xScale}
        width={width - dynamicMargin.left - dynamicMargin.right}
      />
      <ChartAxis.Left
        left={dynamicMargin.left}
        scale={yScale}
        yFormatter={axisFormatter}
        height={height - dynamicMargin.top - dynamicMargin.bottom}
      />

      {/* Tooltip highlights */}
      {tooltipData && !tooltipData.isEvent && (
        <>
          {hasVisibleBarSeries
            ? (() => {
                const barDataset = visibleDatasets[firstVisibleBarSeriesIndex] ?? [];
                if (barDataset.length === 0) {
                  return null;
                }
                const sortedData = [...barDataset].sort((a, b) => a.xValue.getTime() - b.xValue.getTime());
                const currentIndex = sortedData.findIndex((d) => d.xValue.getTime() === tooltipData.xValue.getTime());
                const nextPoint = sortedData[currentIndex + 1];
                const timeInterval =
                  sortedData.length > 1 && nextPoint
                    ? nextPoint.xValue.getTime() - tooltipData.xValue.getTime()
                    : 15 * 60000;

                const barWidth = Math.abs(
                  xScale(new Date(tooltipData.xValue.getTime() + timeInterval)) - xScale(tooltipData.xValue)
                );

                const x = xScale(tooltipData.xValue);
                const barX = x - barWidth / 2;
                const yRange = yScale.range();

                // Clipped to the plot. The highlight is centred on the bar, so the
                // first and last bars put half a bar outside it — over the Y axis on
                // the left, past the frame on the right (ALL-2531).
                const [rangeStart, rangeEnd] = xScale.range() as [number, number];
                const plotLeft = Math.min(rangeStart, rangeEnd);
                const plotRight = Math.max(rangeStart, rangeEnd);
                const clippedX = Math.max(barX, plotLeft);
                const clippedWidth = Math.max(0, Math.min(barX + barWidth, plotRight) - clippedX);

                return (
                  <rect
                    x={clippedX}
                    y={yRange[1]}
                    width={clippedWidth}
                    height={yRange[0] - yRange[1]}
                    className="fill-background-muted"
                    opacity={0.5}
                    pointerEvents="none"
                    style={{ transition: "all 100ms ease-out" }}
                  />
                );
              })()
            : null}
          {tooltipData.series
            .filter((series) => series.type !== "bar" && series.showMarker !== false)
            .map((series) => (
              <g key={`highlight-${series.label}`}>
                <circle
                  cx={xScale(series.xValue ?? tooltipData.xValue)}
                  cy={yScale(series.chartY ?? series.value)}
                  r={8}
                  fill="white"
                  stroke={series.color}
                  strokeWidth={2}
                  style={{ transition: "all 100ms ease-out" }}
                />
                <circle
                  cx={xScale(series.xValue ?? tooltipData.xValue)}
                  cy={yScale(series.chartY ?? series.value)}
                  r={4}
                  fill={series.color}
                  style={{ transition: "all 100ms ease-out" }}
                />
              </g>
            ))}
        </>
      )}

      {/* Interactive area */}
      <rect
        x={dynamicMargin.left}
        y={dynamicMargin.top}
        width={width - dynamicMargin.left - dynamicMargin.right}
        height={height - dynamicMargin.top - dynamicMargin.bottom}
        fill="transparent"
        style={{
          cursor: enableZoom ? "grab" : onPointClick ? "pointer" : "crosshair",
          touchAction: "none",
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handlePointClick}
      />

      {/* Event markers - render on top of interactive area */}
      {eventMarkerChildren}
    </>
  );

  return expand.present(
    <div className="relative flex h-full flex-col">
      <ChartContext.Provider value={contextValue}>
        {header}

        {enableZoom ? (
          <Zoom
            // `initialTransformMatrix` is mount-only, and inside the expand
            // Dialog this mounts before the observer reports the dialog's size —
            // so it seeds using the inline width and the translation is stale
            // the moment the real width arrives. Keying on the dimensions
            // remounts and reseeds at the size actually in effect. A new `xDomain`
            // reseeds the same way, so a kept zoom window is never read against
            // a matrix calibrated for the old domain.
            key={`zoom-${Math.round(width)}x${Math.round(height)}-${xDomain?.[0].getTime() ?? ""}-${xDomain?.[1].getTime() ?? ""}`}
            initialTransformMatrix={transformForDomain(zoomDomain)}
            width={width}
            height={height}
            scaleXMin={1}
            scaleXMax={20}
            scaleYMin={1}
            scaleYMax={1}
            constrain={(transformMatrix) => {
              const { scaleX, translateX } = transformMatrix;

              // Constrain scale
              const constrainedScaleX = Math.max(1, Math.min(20, scaleX));
              const constrainedScaleY = 1; // Keep Y scale at 1

              // Calculate chart dimensions
              const chartWidth = width - dynamicMargin.left - dynamicMargin.right;

              // Constrain translateX to keep chart in bounds
              const maxTranslateX = 0;
              const minTranslateX = chartWidth * (1 - constrainedScaleX);
              const constrainedTranslateX = Math.max(minTranslateX, Math.min(maxTranslateX, translateX));

              return {
                ...transformMatrix,
                scaleX: constrainedScaleX,
                scaleY: constrainedScaleY,
                translateX: constrainedTranslateX,
                translateY: 0,
              };
            }}
          >
            {(zoom) => {
              // Store zoom instance
              zoomRef.current = zoom;

              // Update zoom domain when transform changes (but only if it actually changed)
              if (zoom.transformMatrix) {
                const { scaleX, translateX } = zoom.transformMatrix;
                const transformKey = `${scaleX.toFixed(4)}-${translateX.toFixed(2)}`;

                if (transformKey !== lastTransformRef.current) {
                  lastTransformRef.current = transformKey;
                  // Use setTimeout to avoid updating state during render
                  setTimeout(() => {
                    handleZoomTransform(zoom.transformMatrix);
                  }, 0);
                }
              }

              return (
                <svg
                  width={width}
                  height={height}
                  className="overflow-visible"
                  ref={(node) => {
                    // Set both our ref and visx's containerRef
                    svgRef.current = node;
                    if (zoom.containerRef.current !== node) {
                      // @ts-expect-error - visx containerRef is mutable but types don't reflect this
                      zoom.containerRef.current = node;
                    }
                  }}
                  role="img"
                  aria-label={title}
                  style={{
                    cursor: zoom.isDragging ? "grabbing" : "grab",
                    touchAction: "none",
                  }}
                  onMouseDown={zoom.dragStart}
                  onMouseMove={zoom.dragMove}
                  onMouseUp={zoom.dragEnd}
                  onMouseLeave={() => {
                    if (zoom.isDragging) zoom.dragEnd();
                  }}
                  onTouchStart={zoom.dragStart}
                  onTouchMove={zoom.dragMove}
                  onTouchEnd={zoom.dragEnd}
                >
                  {chartContent}
                </svg>
              );
            }}
          </Zoom>
        ) : (
          <svg width={width} height={height} className="overflow-visible" ref={svgRef} role="img" aria-label={title}>
            {chartContent}
          </svg>
        )}

        <ChartTooltip data={tooltipData} formatter={axisFormatter} />

        {enableBrush && fullDomain && (
          <ChartBrush
            data={visibleDatasets}
            seriesMetadata={visibleSeriesMetadata}
            width={width}
            height={brushHeight}
            onBrushChange={handleBrushChange}
            brushRegion={zoomDomain}
            margin={dynamicMargin}
            xDomain={fullDomain}
            {...(yDomain ? { yDomain } : {})}
          />
        )}

        {!hideBottomBar && (
          <div ref={bottomBarRef} className="flow-root shrink-0" data-chart-bottom-bar-container>
            <ChartBottomBar
              onExport={handleExport}
              items={uniqueSeries}
              isZoomed={zoomDomain !== null}
              onResetZoom={handleResetZoom}
              onLegendItemToggle={categoryInfo ? undefined : handleLegendItemToggle}
              /* Align the bar with the PLOT, not the container. `ChartBottomBar` has taken these
               since it was written and nothing ever passed them, so the first swatch sat against
               the container edge — left of the y axis, touching the axis line — while the plot
               started a tick-label's width further in. `dynamicMargin` is the same measurement the
               scales use, so the legend now starts exactly where the data does. */
              marginLeft={dynamicMargin.left}
              marginRight={dynamicMargin.right}
            />
          </div>
        )}
      </ChartContext.Provider>
    </div>
  );
};

/**
 * ChartContainer
 *
 * Main chart wrapper component that provides context and layout for chart visualizations.
 * Handles scaling, tooltips, axes, and data management for area, line, and bar charts.
 */
// Type assertion to fix React 19 compatibility issue with withParentSize HOC.
// `parentWidth`/`parentHeight` are normally injected by the HOC; they remain
// optionally overridable (e.g. tests, or callers that pre-measure).
const MeasuredChartContainer = withParentSize(VisXChartContainer) as unknown as React.FC<
  Omit<ChartContainerProps, "parentWidth" | "parentHeight"> & {
    parentWidth?: number;
    parentHeight?: number;
  }
>;

export type ChartContainerWithSourceProps = Omit<ChartContainerProps, "parentWidth" | "parentHeight" | "yDomain"> & {
  parentWidth?: number;
  parentHeight?: number;
  /**
   * Explicit y-axis domain — a fixed `[min, max]`, or, while STREAMING, a
   * function of the points currently loaded.
   *
   * ── 🔴 Why streaming needs the callback form ──────────────────────────────
   * A fed chart's caller knows every value it is about to draw, so a tuple is
   * a complete answer. A streaming chart's caller knows NONE of them: its
   * points do not exist until the reader looks at a window. A tuple computed
   * from what the declaration could see — thresholds and bounds, since
   * `series` is empty by construction — is then treated as authoritative by
   * `TiledTimeSeriesChart`, and every fetched value outside it is clipped to
   * the plot edge rather than widening the axis.
   *
   * That failure is the quiet kind: the chart draws, the threshold line sits
   * where it should, and an overload above it is simply not visible. Which is
   * the exact reading a threshold exists to prevent.
   *
   * `TiledTimeSeriesChart` has always accepted the callback; only this type
   * kept it out of reach. The fed engine below still takes the tuple alone —
   * see the guard in `ChartContainer` — because its own scaling already sees
   * all of the data.
   */
  yDomain?: [number, number] | ((data: BaseDataPoint[]) => [number, number]);
  /**
   * Where the data comes from. Omit it — or pass `{ kind: "inline" }` — and this
   * is the chart it has always been, drawing the `data` its series children
   * carry. Pass a streaming source and the same children instead name which
   * stream each draws, and the chart fetches history as the reader moves.
   *
   * See `ChartSource`. The declaration is JSON, so a BLOCK can carry it; the
   * fetcher comes from `ChartSourceProvider` at the render site.
   */
  source?: ChartSource;
  /**
   * Chart configuration that only means anything while streaming.
   *
   * ── 🔴 Why these are grouped rather than flattened ────────────────────────────
   * A resolution ladder, a window-size picker and a preset list describe a chart
   * that GOES AND GETS more data as you move. A fed chart has one window — the
   * one its caller handed it — so on the inline path these props have no
   * meaning at all, not merely no effect. Hoisting them onto
   * `ChartContainerProps` would put six inert options on every chart in the
   * platform and invite exactly the bug this grouping prevents: setting
   * `windowSizeOptions` on a fed chart and wondering why the picker never
   * appears.
   *
   * Grouping also keeps the split honest in the other direction. `hint` and
   * `height` DO have inline meanings elsewhere in the codebase (a block's
   * caption, a reserved box), and flattening them here would silently overload
   * one name with two behaviours depending on a sibling prop. Under `streaming`
   * they are unambiguous: `streaming.hint` is the explorer's how-to-drive line,
   * `streaming.height` is the explorer's plot height.
   *
   * Everything here is JSON-safe, so a block can declare it — which is the
   * point. Without it a streaming block draws with a generic window ladder and
   * no meaningful presets: a chart that renders, looks plausible, and is wrong
   * at exactly the zoom levels people use.
   */
  streaming?: StreamingChartConfig;
};

/**
 * The streaming-only half of a chart's configuration.
 *
 * Every field is optional and JSON-safe. `StreamingChart` already accepts all
 * of them; this type is what lets a caller — and therefore a block — express
 * them through `ChartContainer` rather than by importing the tiled engine
 * directly.
 */
export type StreamingChartConfig = {
  /** Which window size each resolution tier maps to, e.g. `{ "5m": "FIVE_MINUTES" }`. */
  tierToWindow?: React.ComponentProps<typeof StreamingChart>["tierToWindow"];
  /** The window sizes offered in the picker, in ladder order. */
  windowSizeOptions?: React.ComponentProps<typeof StreamingChart>["windowSizeOptions"];
  /** Jump-to-window presets, e.g. Last 24 hours / Last 7 days. */
  presetRanges?: React.ComponentProps<typeof StreamingChart>["presetRanges"];
  /**
   * How to DRIVE the chart, shown under the plot — panning, zooming, presets.
   * Distinct from a block's `description`, which says what is plotted.
   */
  hint?: string;
  /** Shown when the source returns no readings for the visible window. */
  emptyState?: { title: string; description: string };
  /**
   * Plot height in px. Omit to fill the box the caller reserved, which is what
   * a block inside a card wants; see the `fitParent` note in `StreamingChart`.
   */
  height?: number;
  /**
   * Show a built-in "latest data in view" readout beside the resolution line.
   *
   * Named rather than rendered, so a chart driven from stored JSON can have it:
   * `single` reads the primary stream, `band` a net band plus its average line,
   * `pair` an import/export pair.
   */
  latestDataReadout?: React.ComponentProps<typeof StreamingChart>["latestDataReadout"];
  /** Which streams that readout inspects, when the per-kind defaults do not fit. */
  latestDataSeriesKeys?: string[];
  /**
   * Extra plots stacked below this chart, on its camera and its controls.
   *
   * For a view that is several streams of ONE thing — a node's SCADA metric
   * groups, say — where the reader picks a window once and every metric answers
   * for that window. Each panel names its own source and its own series; the
   * window, presets, resolution and reset stay singular above them.
   *
   * Streaming only, like everything else in this group: a fed chart has one
   * window handed to it, so there is no shared camera for panels to share.
   */
  panels?: React.ComponentProps<typeof StreamingChart>["panels"];
};

/**
 * Main chart wrapper. Provides context and layout for chart visualizations, and
 * — since the `source` prop — owns the choice between holding data and
 * streaming it.
 *
 * ── 🔴 Why the streaming branch is a sibling, not a mode ──────────────────────
 * `VisXChartContainer` below reads `child.props.data` synchronously while it
 * renders, and its whole 1,300 lines are built on the data already being there.
 * Threading async loading THROUGH that would put every chart in the platform on
 * a changed code path to serve the few that stream — and the failure that
 * invites is the quiet kind: charts still render, just subtly wrong.
 *
 * So the branch is here, ABOVE the measured component, and it is exhaustive: a
 * chart with no streaming source never reaches a line of new code. That is a
 * property a reviewer can check by reading this function rather than trusting a
 * test, and it is deliberately structural rather than conventional.
 *
 * Folding the two engines together is worth doing, but it is a separate change
 * with its own risk; this one only makes both reachable through one prop.
 */
export const ChartContainer: React.FC<ChartContainerWithSourceProps> = ({ source, streaming, ...props }) => {
  if (isStreamingSource(source)) {
    return (
      <StreamingChart
        source={source}
        title={props.title ?? "Chart"}
        {...(props.yFormatter !== undefined ? { yFormatter: props.yFormatter } : {})}
        {...(props.yDomain !== undefined ? { yDomain: props.yDomain } : {})}
        {...(props.referenceLines !== undefined ? { referenceLines: props.referenceLines } : {})}
        {...(props.headerActions !== undefined ? { headerActions: props.headerActions } : {})}
        /*
         * Forwarded field by field rather than as `{...streaming}`.
         *
         * A spread would behave identically today — `StreamingChart` guards on
         * `height !== undefined` rather than on key presence, so an omitted or
         * explicitly-undefined field lands the same way. The reason to name
         * each one is that this list is the CONTRACT: it is where a reader sees
         * which of the explorer's props a declarative caller can reach, and a
         * spread would quietly widen that surface to whatever the config type
         * happens to grow next. Adding a field should be a deliberate line
         * here, matching how the four props above already read.
         */
        {...(streaming?.tierToWindow !== undefined ? { tierToWindow: streaming.tierToWindow } : {})}
        {...(streaming?.windowSizeOptions !== undefined ? { windowSizeOptions: streaming.windowSizeOptions } : {})}
        {...(streaming?.presetRanges !== undefined ? { presetRanges: streaming.presetRanges } : {})}
        {...(streaming?.hint !== undefined ? { hint: streaming.hint } : {})}
        {...(streaming?.emptyState !== undefined ? { emptyState: streaming.emptyState } : {})}
        {...(streaming?.height !== undefined ? { height: streaming.height } : {})}
        {...(streaming?.latestDataReadout !== undefined ? { latestDataReadout: streaming.latestDataReadout } : {})}
        {...(streaming?.latestDataSeriesKeys !== undefined
          ? { latestDataSeriesKeys: streaming.latestDataSeriesKeys }
          : {})}
        {...(streaming?.panels !== undefined ? { panels: streaming.panels } : {})}
      >
        {props.children}
      </StreamingChart>
    );
  }
  /*
   * 🔴 A callback `yDomain` is dropped on the fed path rather than forwarded.
   *
   * `VisXChartContainer` passes `yDomain` to `createYScale` and to
   * `ChartBrush`, both of which destructure it as a tuple — a function would
   * read as `[undefined, undefined]` and produce a `NaN` scale, which draws an
   * empty plot with axes intact. The callback exists for the streaming branch
   * above, where the points arrive after the caller has answered; a fed chart's
   * caller already knows all of its data, so the tuple is the whole contract
   * and a function here is a caller mistake, not a mode.
   *
   * Dropped to the engine's own scaling, which is the behaviour a chart with no
   * explicit domain has always had, rather than crashing or drawing blank.
   */
  const { yDomain, ...rest } = props;
  return <MeasuredChartContainer {...rest} {...(typeof yDomain === "function" ? {} : { yDomain })} />;
};

ChartContainer.displayName = "ChartContainer";
